import { useEffect } from 'react'
import { useSlsStateContext, useSlsHighlightsContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../providers/r2Provider/R2Context'

/**
 * Custom hook that properly sets highlights on both main and split screen images
 * Follows the pattern from the old PersonOverview.js implementation
 *
 * @param {Object} options
 * @param {Array<string>} options.elementIds - Field IDs to display highlights for
 * @param {boolean} options.enabled - Whether to display highlights (default: true)
 * @returns {void}
 */
export const useDisplayHighlights = ({ elementIds, enabled = true }) => {
  const { currentImageArk } = useSlsStateContext()
  const { setDisplayHighlights, clearDisplay } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2State = useR2StateContext()

  useEffect(() => {
    // Don't set highlights if disabled or if highlights panel is open
    if (!enabled || topMostPanel() === 'highlightsPanel') {
      return
    }

    // Calculate split screen ARK inside effect (like PersonOverview.js does)
    const splitScreenImageArk = thumbnails?.[r2State?.referenceFocusedIndex]?.ark

    if (currentImageArk && elementIds?.length > 0) {
      // Call #1: Set main screen highlights
      setDisplayHighlights({
        mainPartitionKey: currentImageArk,
        elementIds,
      })

      // Call #2: Set split screen highlights (if active)
      // MUST check r2State.splitScreen before making this call
      if (r2State.splitScreen && splitScreenImageArk) {
        setDisplayHighlights({
          splitScreenPartitionKey: splitScreenImageArk,
          elementIds,
        })
      }
    } else if (currentImageArk) {
      // Clear highlights if no element IDs
      clearDisplay({ main: true, splitScreen: true })
    }
  }, [
    enabled,
    currentImageArk,
    r2State.referenceFocusedIndex,
    r2State.splitScreen,
    elementIds,
    setDisplayHighlights,
    clearDisplay,
    topMostPanel,
    thumbnails,
  ])
}
